import messages from "messages";
import moment from "moment-timezone";
import { FunctionComponent, useMemo } from "react";
// components
import DataTransferTag from "../data-transfer-tag";
// types
import type { DataTransferImportTask } from "../../../../types";
// styles
import styles from "./styles.module.scss";
// utils
import getClasses from "utils/getClasses";
// constants
import { IMPORT_SOURCES } from "../../../../constants";

/**
 * column definition for the table, it should have the column string id and field
 */
const COLUMNS: {
  field: keyof DataTransferImportTask;
  stringId: string;
  Element?: FunctionComponent<{ children: string }>;
}[] = [
  {
    field: "name",
    stringId: "fileName",
  },
  {
    field: "createDatetime",
    stringId: "dateImported",
  },
  {
    field: "source",
    stringId: "importedFrom",
  },
  {
    field: "status",
    stringId: "status",
  },
];

/**
 * date format for imported datetime
 */
const DATE_FORMAT = "MMMM DD, YYYY";
/**
 * time format for imported datetime
 */
const TIME_FORMAT = "LT";

type Props = {
  tasks: DataTransferImportTask[];
  timezone: string;
};

/**
 * renders stateless UI layout for recent imports in data transfer page
 */
const ImportedUI = ({ tasks, timezone }: Props) => {
  const tasksWithDateTime = useMemo(
    () =>
      tasks.map((el) => {
        const timeMoment = moment.tz(el.createDatetime, timezone);

        return {
          ...el,
          date: timeMoment.format(DATE_FORMAT),
          time: timeMoment.format(TIME_FORMAT),
        };
      }),
    [tasks, timezone],
  );
  return (
    <div className={styles["imported-ui"]}>
      <table className={styles["imported-ui__table"]}>
        <thead>
          <tr
            className={getClasses(
              styles["imported-ui__tr"],
              styles["imported-ui__tr--th"],
            )}
          >
            {COLUMNS.map(({ stringId, field }) => (
              <th className={styles["imported-ui__th"]} key={field}>
                {messages[stringId]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!tasksWithDateTime.length ? (
            <tr
              className={getClasses(
                styles["imported-ui__tr"],
                styles["imported-ui__tr--no-data"],
              )}
            >
              <td colSpan={COLUMNS.length}>{messages.noDataimports}</td>
            </tr>
          ) : (
            tasksWithDateTime.map((el, i) => (
              <tr
                key={i}
                className={getClasses(
                  styles["imported-ui__tr"],
                  styles["imported-ui__tr--tbody"],
                )}
              >
                {COLUMNS.map(({ field }) => (
                  <td key={field}>
                    {field === "status" ? (
                      <DataTransferTag value={el[field]} />
                    ) : field === "createDatetime" ? (
                      <>
                        <span className={styles["imported-ui__date"]}>
                          {el.date}
                        </span>
                        <span className={styles["imported-ui__time"]}>
                          {el.time}
                        </span>
                      </>
                    ) : field === "source" ? (
                      IMPORT_SOURCES[el.source].name
                    ) : (
                      el[field]
                    )}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ImportedUI;
